import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Stack,
} from '@mui/material';
import {
  Close as CloseIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
  BugReport as BugReportIcon,
  Lightbulb as FeatureIcon,
  Help as QuestionIcon,
} from '@mui/icons-material';
import TiptapEditor from './TiptapEditor';

interface FeedbackDialogProps {
  open: boolean;
  onClose: () => void;
  sourcePortal: string;
  /** Pre-fill email if user is logged in */
  userEmail?: string;
  /** Pre-fill Qixit username if user is logged in */
  summaUsername?: string;
  /** Pre-fill customer name */
  customerName?: string;
}

interface Attachment {
  file: File;
  preview: string;
}

type FeedbackType = 'Bug' | 'Feature' | 'Question' | 'Other';
type ResponsePreference = 'Email' | 'SummaInbox' | 'NoResponseNeeded';
type Priority = 'Low' | 'Normal' | 'High';

const resolveFeedbackApiBase = (): string => {
  const envBase = (import.meta.env.VITE_FEEDBACK_API_BASE_URL || '').trim();
  if (envBase) {
    return envBase.replace(/\/$/, '');
  }

  const host = window.location.hostname.toLowerCase();
  if (host === 'localhost' || host === '127.0.0.1') {
    return 'http://localhost:5180';
  }
  if (host.startsWith('st') && host.endsWith('.qixit.com')) {
    return 'https://customerservice-api-stage-611e.azurewebsites.net';
  }

  return 'https://support.qixit.com';
};

const FEEDBACK_API_URL = `${resolveFeedbackApiBase()}/api/feedback/submit`;

const resolveLoggedInUsername = (): string => {
  const possibleKeys = ['consumerUsername', 'username', 'loginAlias'];
  for (const key of possibleKeys) {
    const value = localStorage.getItem(key);
    if (value && value.trim().length > 0) {
      return value.trim();
    }
  }
  return '';
};

export default function FeedbackDialog({
  open,
  onClose,
  sourcePortal,
  userEmail = '',
  summaUsername = '',
  customerName = '',
}: FeedbackDialogProps) {
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('Bug');
  const [priority, setPriority] = useState<Priority>('Normal');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState(userEmail);
  const [username, setUsername] = useState(summaUsername || resolveLoggedInUsername());
  const [name, setName] = useState(customerName);
  const [responsePreference, setResponsePreference] = useState<ResponsePreference>('Email');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const getPlainText = (html: string): string =>
    html
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  useEffect(() => {
    if (!open) return;
    setEmail(userEmail);
    setUsername(summaUsername || resolveLoggedInUsername());
    setName(customerName);
  }, [open, userEmail, summaUsername, customerName]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const newAttachments: Attachment[] = [];
    
    for (let i = 0; i < files.length && attachments.length + newAttachments.length < 3; i++) {
      const file = files[i];
      
      // Only allow images or PDF files
      if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
        setError('Only image or PDF files are allowed');
        continue;
      }
      
      // Check size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        continue;
      }
      
      newAttachments.push({
        file,
        preview: URL.createObjectURL(file),
      });
    }
    
    setAttachments([...attachments, ...newAttachments]);
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (index: number) => {
    const newAttachments = [...attachments];
    URL.revokeObjectURL(newAttachments[index].preview);
    newAttachments.splice(index, 1);
    setAttachments(newAttachments);
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        // Remove data URL prefix (e.g., "data:image/png;base64,")
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
    });
  };

  const handleSubmit = async () => {
    setError(null);
    setSuccess(null);

    // Validation
    if (!subject.trim()) {
      setError('Please enter a subject');
      return;
    }
    if (!getPlainText(description)) {
      setError('Please describe the issue or request');
      return;
    }
    if (responsePreference !== 'NoResponseNeeded' && !email && !username) {
      setError('Please provide an email or Qixit username for response');
      return;
    }

    setSubmitting(true);

    try {
      // Convert attachments to base64
      const attachmentDtos = await Promise.all(
        attachments.map(async (att) => ({
          filename: att.file.name,
          contentType: att.file.type,
          base64Content: await fileToBase64(att.file),
        }))
      );

      const feedbackData = {
        sourcePortal,
        feedbackType: feedbackType === 'Bug' ? 1 : feedbackType === 'Feature' ? 2 : feedbackType === 'Question' ? 3 : 4,
        priority: priority === 'Low' ? 3 : priority === 'Normal' ? 2 : 1, // 1=High, 2=Normal, 3=Low
        subject,
        description,
        customerEmail: email || null,
        summaUsername: username || null,
        customerName: name || null,
        responsePreference: responsePreference === 'Email' ? 1 : responsePreference === 'SummaInbox' ? 2 : 3,
        browserInfo: navigator.userAgent,
        currentUrl: window.location.href,
        attachments: attachmentDtos.length > 0 ? attachmentDtos : null,
      };

      const response = await fetch(FEEDBACK_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(feedbackData),
      });

      if (!response.ok) {
        // Try to get error details from response
        let errorMessage = `Server error: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch {
          // Response wasn't JSON
        }
        setError(errorMessage);
        return;
      }

      const result = await response.json();

      if (result.success) {
        setSuccess(`Thank you! Your feedback has been submitted. Ticket: ${result.ticketNumber}`);
        // Clear form after success
        setTimeout(() => {
          resetForm();
          onClose();
        }, 2000);
      } else {
        setError(result.message || 'Failed to submit feedback');
      }
    } catch (err) {
      console.error('Error submitting feedback:', err);
      // Check if it's a network error (API not running)
      if (err instanceof TypeError && err.message.includes('fetch')) {
        setError(`Cannot connect to feedback service at ${FEEDBACK_API_URL}.`);
      } else {
        setError(`Failed to submit feedback: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFeedbackType('Bug');
    setPriority('Normal');
    setSubject('');
    setDescription('');
    setEmail(userEmail);
    setUsername(summaUsername || resolveLoggedInUsername());
    setName(customerName);
    setResponsePreference('Email');
    attachments.forEach(att => URL.revokeObjectURL(att.preview));
    setAttachments([]);
    setError(null);
    setSuccess(null);
  };

  const handleClose = () => {
    if (!submitting) {
      resetForm();
      onClose();
    }
  };

  const getFeedbackIcon = (type: FeedbackType) => {
    switch (type) {
      case 'Bug': return <BugReportIcon color="error" />;
      case 'Feature': return <FeatureIcon color="primary" />;
      case 'Question': return <QuestionIcon color="info" />;
      default: return null;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BugReportIcon />
          Support, or Feature Request or Report Bug
        </Box>
        <IconButton onClick={handleClose} disabled={submitting}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        
        <Stack spacing={2}>
          {/* Feedback Type */}
          <FormControl fullWidth>
            <InputLabel>Type</InputLabel>
            <Select
              value={feedbackType}
              label="Type"
              onChange={(e) => setFeedbackType(e.target.value as FeedbackType)}
              disabled={submitting}
            >
              <MenuItem value="Bug">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BugReportIcon color="error" fontSize="small" />
                  Bug Report
                </Box>
              </MenuItem>
              <MenuItem value="Feature">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FeatureIcon color="primary" fontSize="small" />
                  Feature Request
                </Box>
              </MenuItem>
              <MenuItem value="Question">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <QuestionIcon color="info" fontSize="small" />
                  Question
                </Box>
              </MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>

          {/* Priority */}
          <FormControl fullWidth>
            <InputLabel>Priority</InputLabel>
            <Select
              value={priority}
              label="Priority"
              onChange={(e) => setPriority(e.target.value as Priority)}
              disabled={submitting}
            >
              <MenuItem value="Low">Low - Minor issue or nice-to-have</MenuItem>
              <MenuItem value="Normal">Normal - Standard priority</MenuItem>
              <MenuItem value="High">High - Urgent or blocking issue</MenuItem>
            </Select>
          </FormControl>

          {/* Subject */}
          <TextField
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            fullWidth
            required
            disabled={submitting}
            placeholder={feedbackType === 'Bug' ? 'Brief description of the bug' : 'Brief description of your request'}
          />

          {/* Description */}
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Description
            </Typography>
            <TiptapEditor
              value={description}
              onChange={setDescription}
              disabled={submitting}
              placeholder={
                feedbackType === 'Bug'
                  ? 'Steps to reproduce, expected behavior, and actual behavior...'
                  : 'Please describe your request in detail...'
              }
              minHeight={220}
            />
          </Box>

          {/* Contact Information */}
          <Typography variant="subtitle2" color="text.secondary">
            Contact Information (for response)
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              disabled={submitting}
              placeholder="your@email.com"
            />
            <TextField
              label="Qixit Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              fullWidth
              disabled={submitting}
              placeholder="Qixit Username"
            />
          </Box>

          <TextField
            label="Your Name (optional)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            disabled={submitting}
          />

          {/* Response Preference */}
          <FormControl fullWidth>
            <InputLabel>Preferred Response Method</InputLabel>
            <Select
              value={responsePreference}
              label="Preferred Response Method"
              onChange={(e) => setResponsePreference(e.target.value as ResponsePreference)}
              disabled={submitting}
            >
              <MenuItem value="Email">Email</MenuItem>
              <MenuItem value="SummaInbox">Summa Inbox Message</MenuItem>
              <MenuItem value="NoResponseNeeded">No Response Needed</MenuItem>
            </Select>
          </FormControl>

          {/* Attachments */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Attachments (images or PDF, optional, max 3)
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Allowed: PDF, PNG, JPG/JPEG, GIF, WEBP, BMP (max 5MB each).
            </Typography>
            
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept="image/*,.pdf,application/pdf"
              multiple
              style={{ display: 'none' }}
            />
            
            <Button
              variant="outlined"
              startIcon={<AttachFileIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={submitting || attachments.length >= 3}
              size="small"
            >
              Attach File
            </Button>
            
            {attachments.length > 0 && (
              <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {attachments.map((att, index) => (
                  <Chip
                    key={index}
                    label={att.file.name}
                    onDelete={() => removeAttachment(index)}
                    deleteIcon={<DeleteIcon />}
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Box>
            )}
          </Box>
        </Stack>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={20} /> : getFeedbackIcon(feedbackType)}
        >
          {submitting ? 'Submitting...' : 'Submit Feedback'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
